import { apiClient } from './client';

/*
 * Assumed roles are scoped per area and must only ever be sent to the
 * endpoints of their own area. Each scope keeps its own roles string
 * (several roles semicolon-separated), written by the context of that
 * area, plus a matcher deciding which request URLs it applies to. The
 * roles live outside React so the axios interceptor below can read
 * them. The matchers are disjoint — per request at most one scope
 * applies:
 *
 * - partner scope: the admin role of the holder person of the partner
 *   selected in the sidebar; goes to the office and accounts endpoints
 *   and to the project LIST (which is filtered by partner), but never
 *   to the requests below a project.
 * - project scope: the admin roles of the entered projects; goes to
 *   everything below a project (booking items, hosting assets), but
 *   not to the project list.
 */
export const PARTNER_ASSUME_SCOPE = 'partner';
export const PROJECT_ASSUME_SCOPE = 'project';
type AssumeScope = typeof PARTNER_ASSUME_SCOPE | typeof PROJECT_ASSUME_SCOPE;

type ScopeEntry = {
  appliesTo: (url: string, method: string) => boolean;
  roles: string | null;
};

const isProjectList = (url: string) => url.startsWith('/booking/projects');

/* the sidebar selector must always see every visible partner — with
   the partner role attached its own list would collapse to the
   selected partner and there would be no way back. Only the list
   itself though: the single-partner GET below the same path must ride
   with the assumed role — its embedded `details` are only filled from
   the relation-AGENT level upwards. */
const isPartnerList = (url: string) => /^\/office\/partners(\?|$)/.test(url);

/* The office master data — partners, memberships, debitors — may only
   be written by a global admin, and a global admin writes as
   themselves: an assumed partner role would narrow the permissions of
   the request until the backend refused it. Reading the same records
   keeps the role; that is what makes the sidebar selection work. */
const isMasterDataWrite = (url: string, method: string) =>
  method !== 'get' &&
  /^\/office\/(partners|memberships|debitors)(\/|\?|$)/.test(url);

const assumeScopes: Record<AssumeScope, ScopeEntry> = {
  [PARTNER_ASSUME_SCOPE]: {
    appliesTo: (url: string, method: string) =>
      ((url.startsWith('/office/') && !isPartnerList(url)) ||
        url.startsWith('/accounts/') ||
        isProjectList(url)) &&
      !isMasterDataWrite(url, method),
    roles: null,
  },
  [PROJECT_ASSUME_SCOPE]: {
    /* Bookings live under /booking, HostingAssets under /hosting */
    appliesTo: (url: string) =>
      url.startsWith('/hosting/') ||
      (url.startsWith('/booking/') && !isProjectList(url)),
    roles: null,
  },
};

export function setAssumedRoles(
  scope: string,
  roles: string | null | undefined
): void {
  const entry: ScopeEntry | undefined = assumeScopes[scope as AssumeScope];
  if (!entry) {
    throw new Error(`unknown assume scope: ${scope}`);
  }
  entry.roles = roles || null;
}

apiClient.interceptors.request.use((config) => {
  const url = config.url ?? '';
  const method = (config.method ?? 'get').toLowerCase();
  const active = Object.values(assumeScopes).find(
    (scope) => scope.roles && scope.appliesTo(url, method)
  );
  if (active) {
    config.headers['Hostsharing-Assumed-Roles'] = active.roles;
  }
  return config;
});
